///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sea surface temperature under the storm column: the part of a global
/// CoralTemp frame (SstTable) covering the block footprint, decoded to a
/// CPU grid for the Environment facts and uploaded as an R8 texture for the
/// slab faces and the 26.5 °C isotherm on the floor. Nearest-neighbour
/// resampling so land (byte 0) never bleeds into the water values.
///

import { type SstFrameRecord } from "@/earthbrowser/dataset-distribution/sst/SstTable"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { cCacheAgeOneWeek } from "@/earthbrowser/utils/loaders/FetchLoader"
import { datasetUrl } from "@/utils/datasetUrl"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cGridSize = 192

export interface LatLonBox {
	minLon: number
	minLat: number
	maxLon: number
	maxLat: number
}

export class StormSstField {
	public readonly size = cGridSize
	/** Encoded bytes, row-major north row first: 0 = land/nodata, 1..255 = vmin..vmax */
	public readonly data: Uint8Array
	public readonly bounds: LatLonBox
	public readonly vmin: number
	public readonly vmax: number
	/** Analysis time of the frame, epoch ms */
	public readonly timeMs: number
	public readonly timeIso: string
	private texture?: Texture

	private constructor(record: SstFrameRecord, bounds: LatLonBox, data: Uint8Array) {
		this.data = data
		this.bounds = bounds
		this.vmin = record.vmin
		this.vmax = record.vmax
		this.timeIso = record.data_timestamp
		this.timeMs = Date.parse(record.data_timestamp)
	}

	/**
	 * Cut the region of a global equirectangular frame; undefined on
	 * failure. The region is clamped to the frame, so a storm straddling
	 * the antimeridian gets the part on its center's side
	 */
	public static async load(record: SstFrameRecord, region: LatLonBox): Promise<StormSstField | undefined> {
		const loader = new ImageLoader(datasetUrl(record.image_url), { lifetime: cCacheAgeOneWeek, priority: "high" })
		const image = await loader.loadImage()
		if (!image) return undefined
		const w = image.width
		const h = image.height
		const minLon = Math.max(-180, region.minLon)
		const maxLon = Math.min(180, region.maxLon)
		const minLat = Math.max(-90, region.minLat)
		const maxLat = Math.min(90, region.maxLat)
		if (maxLon - minLon < 0.5 || maxLat - minLat < 0.5) return undefined
		const sx = (minLon + 180) / 360 * w
		const sw = (maxLon - minLon) / 360 * w
		const sy = (90 - maxLat) / 180 * h
		const sh = (maxLat - minLat) / 180 * h

		const canvas = new OffscreenCanvas(cGridSize, cGridSize)
		const ctx = canvas.getContext("2d", { willReadFrequently: true })
		if (!ctx) return undefined
		ctx.imageSmoothingEnabled = false
		ctx.drawImage(image, sx, sy, sw, sh, 0, 0, cGridSize, cGridSize)
		const pixels = ctx.getImageData(0, 0, cGridSize, cGridSize).data
		const data = new Uint8Array(cGridSize * cGridSize)
		for (let i = 0; i < data.length; i++) {
			data[i] = pixels[i * 4]
		}
		return new StormSstField(record, { minLon, minLat, maxLon, maxLat }, data)
	}

	/** Temperature in °C at a lat/lon (nearest cell); NaN over land, nodata or outside the region */
	public sample(lat: number, lon: number): number {
		const b = this.bounds
		const u = (lon - b.minLon) / (b.maxLon - b.minLon)
		const v = (b.maxLat - lat) / (b.maxLat - b.minLat)
		if (u < 0 || u >= 1 || v < 0 || v >= 1) return Number.NaN
		const n = this.size
		const byte = this.data[Math.floor(v * n) * n + Math.floor(u * n)]
		if (byte === 0) return Number.NaN
		return this.vmin + (this.vmax - this.vmin) * (byte - 1) / 254
	}

	/**
	 * Temperature at a lat/lon, or the nearest water within `radiusCells`
	 * when the cell itself is land (a center fixed over a coast)
	 */
	public sampleNearWater(lat: number, lon: number, radiusCells: number = 3): number {
		const direct = this.sample(lat, lon)
		if (Number.isFinite(direct)) return direct
		const b = this.bounds
		const dLat = (b.maxLat - b.minLat) / this.size
		const dLon = (b.maxLon - b.minLon) / this.size
		for (let r = 1; r <= radiusCells; r++) {
			for (let j = -r; j <= r; j++) {
				for (let i = -r; i <= r; i++) {
					if (Math.max(Math.abs(i), Math.abs(j)) !== r) continue
					const t = this.sample(lat + j * dLat, lon + i * dLon)
					if (Number.isFinite(t)) return t
				}
			}
		}
		return Number.NaN
	}

	/** R8 texture of the encoded region (nearest filtering; the shader decodes) */
	public getTexture(context: StovokorContext): Texture {
		if (!this.texture) {
			this.texture = new Texture(context, 1, 1, InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte,
				TextureFilter.Nearest, TextureFilter.Nearest, TextureWrap.ClampToEdge, TextureWrap.ClampToEdge)
			this.texture.uploadRawData(this.data, cGridSize, cGridSize, InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte)
		}
		return this.texture
	}

	public destroy(): void {
		this.texture?.destroy()
		this.texture = undefined
	}
}
